import MainLayout from "@/components/layouts/MainLayout";
import Stepper from "../../panel/buypanels/components/Stepper";
import ScrollToTop from "@/components/ScrollToTop";
import { useCurrentPath } from "@/hooks/useCurrentPath";
import LoadingSection from "@/components/LoadingSection";
import { useBuyPanelsStore } from "../../panel/buypanels/store";
import { useValidateStep } from "../../panel/buypanels/hook";
import React, { ReactNode, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router";
import BackButton from "../ui/BackButton";
import { useGeoCountry } from "@/queries/countries/useGeoCountry";
import { useCountries } from "@/hooks/Countries";
import UnavailableInCountry from "../UnavailableInCountry";
import Testimonials from "@/components/Testimonials";
import Divider from "../ui/Divider";
import FuturePaymentMethods from "../FuturePaymentMethods";
import PaymentMethods from "../PaymentMethods";
import HelpSection from "../../panel/buypanels/components/HelpSection";
import { useOrderUuid, useHasOrderUuidHydrated } from "@/stores/orderIdStore";
import { useNewOrder } from "@/queries/order/useNewOrder";
import ExitEmailDialog from "../../panel/buypanels/components/ExitEmailDialog";
import { useExitEmail } from "../../panel/buypanels/hooks/useExitEmail";
import { DefaultService } from "@/api/SolarCloudApi";
import { getBuyPanelsRootSlug } from "../../panel/buypanels/route";

interface BuyPanelsLayoutProps {
  children: ReactNode;
}

export default function BuyPanelsLayout({
  children,
}: BuyPanelsLayoutProps) {
  useValidateStep(); // validate the current step when the layout mounts
  const currentPath = useCurrentPath();
  const hasHydrated = useBuyPanelsStore((state) => state._hasHydrated);
  const getPreviousStep = useBuyPanelsStore((state) => state.getPreviousStep);
  const navigate = useNavigate();
  const exitEmail = useExitEmail();
  // Feature flag: set to `true` to enable exit popup behavior again
  const EXIT_POPUP_ENABLED = false;
  const { data: country } = useGeoCountry();
  const { countries } = useCountries();
  const location = useLocation();
  const isOnConfirmation = location.pathname.includes("confirmation");
  const rootSlug = getBuyPanelsRootSlug(location.pathname);
  const isNewLandingPage = rootSlug === "evsolar" || rootSlug === "corporatesolar";
  const isLandingPage = currentPath === rootSlug || currentPath === "";
  const orderUuid = useOrderUuid();
  const orderUuidHydrated = useHasOrderUuidHydrated();

  const { data: order } = useNewOrder({
    uuid: orderUuid,
    queryConfig: {
      enabled: orderUuidHydrated && !!orderUuid && !isOnConfirmation,
    },
  });

  useEffect(() => {
    console.debug("BuyPanelsLayout: orderUuidHydrated=", orderUuidHydrated, "orderUuid=", orderUuid, "isOnConfirmation=", isOnConfirmation);
  }, [orderUuidHydrated, orderUuid, isOnConfirmation]);
  const buyPanelsOrder = useBuyPanelsStore((s) => s.data.order);

  const hasItems =
    (order && Array.isArray(order.items) && order.items.length > 0) ||
    (buyPanelsOrder && Array.isArray(buyPanelsOrder.items) && buyPanelsOrder.items.length > 0);

  const hasTriggeredRef = useRef(false);

  useEffect(() => {
    if (!EXIT_POPUP_ENABLED) return;
    if (!hasItems || exitEmail.isOpen) return;

    const handleMouseLeave = (e: MouseEvent) => {
      if (e.clientY <= 0 && !hasTriggeredRef.current) {
        // eslint-disable-next-line no-console
        hasTriggeredRef.current = true;
        exitEmail.open();
      }
    };

    document.addEventListener("mouseleave", handleMouseLeave);
    return () => document.removeEventListener("mouseleave", handleMouseLeave);
  }, [hasItems, exitEmail]);

  const handleBack = () => {
    navigate(getPreviousStep());
  };

  const isCountryAvailable =
    !country || !countries
      ? true
      : countries.some((c) => c.code === country.code);

  if (!hasHydrated) {
    return <LoadingSection />;
  }

  if (country && !isCountryAvailable) {
    return (
      <>
        <ScrollToTop />
        <MainLayout>
          <UnavailableInCountry country={country} countries={countries} />
        </MainLayout>
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <MainLayout>
        {!(
          currentPath === "confirmation" ||
          currentPath === rootSlug ||
          currentPath === "ownership" ||
          currentPath === "payment" ||
          currentPath === "manual-payment" ||
          currentPath === "success" ||
          currentPath === "payment-return" ||
          currentPath === ""
        ) && <BackButton handleBack={handleBack} className="justify-start" />}
        {!(isNewLandingPage && isLandingPage) && (
          <Stepper
            className={
              currentPath === "confirmation" ||
                currentPath === rootSlug ||
                currentPath === ""
                ? "mb-6 md:mb-12"
                : ""
            }
          />
        )}

        <div>{children}</div>
        {currentPath !== "confirmation" && currentPath !== rootSlug && currentPath !== "" && (
          <div className="mx-auto pb-4 max-w-4xl">
            <HelpSection />
          </div>
        )}
        {currentPath !== "confirmation" && (
          <>
            <Divider className="my-4 border-brand-orange-600 max-w-7xl mx-auto" />
            <div className="max-w-7xl mx-auto py-4 px-4">
              <h3 className="text-2xl font-semibold text-muted-foreground mb-4">Pay your way...</h3>
              <PaymentMethods />
            </div>
            <div className="max-w-7xl mx-auto py-4 px-4">
              <Testimonials />
            </div>
            <Divider className="my-4 border-brand-orange-600 max-w-7xl mx-auto" />
            <div className="max-w-2xl mx-auto py-4">
              {/* <PaymentOptions /> */}
              {/* <FuturePaymentMethods /> */}
            </div>
          </>
        )}
        <ExitEmailDialog
          isOpen={exitEmail.isOpen}
          onClose={() => {
            hasTriggeredRef.current = false;
            exitEmail.close();
          }}
          onSubmit={async (email: string) => {
            try {
              localStorage.setItem("buypanels-exit-email", email);
              try {
                await DefaultService.registerContact({ email } as any);
              } catch (err) {
              }
            } finally {
              exitEmail.confirm();
            }
          }}
        />
      </MainLayout>
    </>
  );
}
